/**
 * Minimal structural view of a react-navigation state node. The real `NavigationState` type is
 * navigator-specific; this predicate only needs the focused-route chain, so it reads the three
 * fields defensively and treats anything malformed as "no history".
 */
type NavigationStateLike = {
  index?: number;
  routes?: readonly { state?: unknown }[];
  type?: string;
};

function isNavigationStateLike(value: unknown): value is NavigationStateLike {
  return (
    typeof value === "object" &&
    value !== null &&
    "routes" in value &&
    Array.isArray((value as { routes?: unknown }).routes)
  );
}

/**
 * Whether a `GO_BACK` dispatched right now would pop a screen WITHOUT crossing the bottom-tab
 * boundary — the only kind of "back" our back controls are allowed to perform.
 *
 * `router.canGoBack()` walks the whole navigation tree, so unrelated history OUTSIDE the focused
 * bottom tab (a root-stack entry, or the tab navigator's own tab-switch handling) makes it report
 * `true` even though `router.back()` would yank the user to a different bottom tab or out of the
 * `(app)` group entirely. This predicate instead mirrors react-navigation's actual GO_BACK
 * dispatch semantics (deepest focused navigator first, bubbling up) under this app's invariant
 * that tab-style navigators never handle GO_BACK (`backBehavior="none"` on the bottom tabs and
 * the morpheme-panels tabs):
 *
 * - Focused screen is INSIDE a tab navigator: back is allowed only when some stack strictly
 *   below the (outermost) tab navigator has a previous entry — GO_BACK then pops within the tab.
 * - Focused screen is OUTSIDE any tab navigator (auth / terms / privacy at the `(nav)` level):
 *   back is allowed when any stack on the focused chain has a previous entry, matching plain
 *   `canGoBack()` there.
 */
export function canGoBackWithinTabScope(rootState: unknown): boolean {
  let hasSeenTabNavigator = false;
  let hasPoppableHistoryOutsideTabs = false;
  let node: unknown = rootState;

  while (isNavigationStateLike(node)) {
    const index = node.index ?? 0;
    // Tab-style routers (bottom tabs, material top tabs, drawers) are boundaries, never history:
    // their index>0 means "a non-first tab is focused", and popping that is exactly the
    // cross-tab back this predicate exists to forbid.
    const isTabStyleNavigator = node.type === "tab" || node.type === "drawer";

    if (isTabStyleNavigator) {
      hasSeenTabNavigator = true;
    } else if (index > 0) {
      if (hasSeenTabNavigator) {
        // A stack strictly inside the focused tab has a previous entry. GO_BACK is handled
        // deepest-first, and every navigator deeper than this one is either another in-tab
        // stack (also fine) or a tab-style navigator that can't handle GO_BACK — so the pop
        // stays inside the tab.
        return true;
      }
      hasPoppableHistoryOutsideTabs = true;
    }

    node = node.routes?.[index]?.state;
  }

  return hasSeenTabNavigator ? false : hasPoppableHistoryOutsideTabs;
}
